import React, { createContext, useReducer, useMemo } from "react";
import { GroupSeries, Report, Query, stringifyQuery, customGroupSeriesFor } from "../../lib/api/charts";
import { Loading } from "../base";
import { CustomDashboardMeasures } from "../../charts/CustomDashReports";

type GroupSeriesSet = { series: GroupSeries; loading: Loading; custom: any };

export type CustomGroupChartContextState = {
  series: {
    [index: string]: GroupSeriesSet;
  };
  getGroupSeries(report: Report, query: Query, custom: any): Partial<GroupSeriesSet>;
  retryGroupSeries(report: Report, query: Query, custom: any): void;
};

export const CustomGroupChartContext = createContext<CustomGroupChartContextState>({
  series: {},
  getGroupSeries: () => ({}),
  retryGroupSeries: () => {},
});

type QueryAction =
  | { type: "PATCH_SERIES"; id: string; data: Partial<GroupSeriesSet> }
  | { type: "CLEAR_SERIES"; id: string };

const defaultCustom: string = "Overall Dealership > REVENUE > Total Dealership Sales";

const defaultGroupSeries: GroupSeriesSet = {
  series: { name: "", rows: [], classification: "income", type: "currency", dealers: [], formats: { pos: "", neg: "" } },
  loading: { loaded: false, loading: true, error: null },
  custom: CustomDashboardMeasures[defaultCustom].getMeasure(defaultCustom, CustomDashboardMeasures),
};

export const CustomGroupChartProvider = ({ children }) => {
  const memo = (report: Report, query: Query, custom: any) => `${report}-${stringifyQuery(query)}-${JSON.stringify(custom)}`;

  const [series, dispatchGroupSeries] = useReducer((state: { [index: string]: GroupSeriesSet }, action: QueryAction) => {
    switch (action.type) {
      case "PATCH_SERIES":
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            ...action.data,
          },
        };
      case "CLEAR_SERIES": {
        const { [action.id]: _, ...rest } = state;
        return rest;
      }
      default:
        return state;
    }
  }, {});

  const value = useMemo(
    () => ({
      series,
      retryGroupSeries: (report: Report, query: Query, custom: any) => {
        const id = memo(report, query, custom);
        dispatchGroupSeries({ type: "CLEAR_SERIES", id });
      },
      getGroupSeries: (report: Report, query: Query, custom: any) => {
        const id = memo(report, query, custom);
        const set = series[id];

        if (set) {
          return set;
        }

        dispatchGroupSeries({
          type: "PATCH_SERIES",
          id,
          data: defaultGroupSeries,
        });

        customGroupSeriesFor(query, report, custom)
          .then(series => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                series: series,
                custom: custom,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                loading: {
                  loaded: true,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultGroupSeries;
      },
    }),
    [series],
  );

  return <CustomGroupChartContext.Provider value={value}>{children}</CustomGroupChartContext.Provider>;
};
